import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import apiService from '../services/apiService';
import './InfoPage.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Toolbar configuration: formatting buttons
 */
const TOOLBAR_BUTTONS = [
  { cmd: 'bold',          icon: 'B',  style: { fontWeight: 'bold' },           title: 'Fett' },
  { cmd: 'italic',        icon: 'I',  style: { fontStyle: 'italic' },           title: 'Kursiv' },
  { cmd: 'underline',     icon: 'U',  style: { textDecoration: 'underline' },   title: 'Unterstrichen' },
  { cmd: 'strikeThrough', icon: 'S',  style: { textDecoration: 'line-through' },title: 'Durchgestrichen' },
  { type: 'separator' },
  { cmd: 'justifyLeft',   icon: '\u2261', iconClass: 'align-icon align-left',   title: 'Linksbündig' },
  { cmd: 'justifyCenter', icon: '\u2261', iconClass: 'align-icon align-center', title: 'Zentriert' },
  { cmd: 'justifyRight',  icon: '\u2261', iconClass: 'align-icon align-right',  title: 'Rechtsbündig' },
  { cmd: 'justifyFull',   icon: '\u2261', iconClass: 'align-icon align-justify',title: 'Blocksatz' },
];

/**
 * Info page
 * Displays a freely editable rich-text area.
 * On save, the HTML content is stored permanently in info_content.txt on the server.
 */
function InfoPage() {
  const { t } = useLanguage();

  const [html, setHtml]             = useState('');
  const [isEditing, setIsEditing]   = useState(false);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeFormats, setActiveFormats]   = useState({});

  const editorRef  = useRef(null);
  const contentRef  = useRef(null);

  // Load saved content
  useEffect(() => {
    loadInfo();
  }, []);

  // When editor opens: insert HTML
  useEffect(() => {
    if (isEditing && editorRef.current) {
      editorRef.current.innerHTML = html;
      editorRef.current.focus();
    }
  }, [isEditing]);

  const loadInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getInfo();
      setHtml(data.text || '');
    } catch (err) {
      setError(err.message || t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  // Detect active formats (for button highlighting)
  const updateActiveFormats = useCallback(() => {
    const formats = {};
    ['bold','italic','underline','strikeThrough',
     'justifyLeft','justifyCenter','justifyRight','justifyFull'
    ].forEach(cmd => {
      try { formats[cmd] = document.queryCommandState(cmd); } catch(e) {}
    });
    setActiveFormats(formats);
  }, []);

  const execFormat = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
    updateActiveFormats();
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSaveClick = async () => {
    const content = editorRef.current?.innerHTML || '';
    try {
      setSaving(true);
      setError(null);
      await apiService.saveInfo(content);
      setHtml(content);
      setIsEditing(false);
      showSuccess(t('info.savedSuccess'));
    } catch (err) {
      setError(err.message || t('messages.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    const element = contentRef.current;
    if (!element) return;

    try {
      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin    = 15;
      const pageW     = pdf.internal.pageSize.getWidth();
      const pageH     = pdf.internal.pageSize.getHeight();
      const imgW      = pageW - 2 * margin;
      const imgH      = (canvas.height * imgW) / canvas.width;
      const usableH   = pageH - 2 * margin;

      let heightLeft  = imgH;
      let yOffset     = 0;

      pdf.addImage(imgData, 'PNG', margin, margin - yOffset, imgW, imgH);
      heightLeft -= usableH;

      while (heightLeft > 0) {
        yOffset += usableH;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, margin - yOffset, imgW, imgH);
        heightLeft -= usableH;
      }

      pdf.save('Vereinbarungen.pdf');
    } catch (err) {
      console.error('PDF-Erstellung fehlgeschlagen:', err);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  if (loading) {
    return <div className="loading">{t('messages.loading')}</div>;
  }

  return (
    <div className="info-page">
      <div className="info-header">
        <h2>{t('info.title')}</h2>
        {!isEditing && (
          <button className="btn btn-primary" onClick={handleEditClick}>
            {t('buttons.edit')}
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          <strong>{t('messages.error')}</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <strong>{t('messages.success')}</strong> {successMessage}
        </div>
      )}

      {isEditing ? (
        <div className="info-edit">
          {/* Toolbar */}
          <div className="rte-toolbar">
            {TOOLBAR_BUTTONS.map((btn, i) =>
              btn.type === 'separator'
                ? <span key={i} className="rte-separator" />
                : (
                  <button
                    key={btn.cmd}
                    type="button"
                    className={`rte-btn${activeFormats[btn.cmd] ? ' rte-btn--active' : ''}`}
                    title={btn.title}
                    onMouseDown={(e) => {
                      e.preventDefault(); // prevent losing focus
                      execFormat(btn.cmd);
                    }}
                  >
                    {btn.iconClass
                      ? <span className={btn.iconClass}>{btn.icon}</span>
                      : <span style={btn.style}>{btn.icon}</span>
                    }
                  </button>
                )
            )}
          </div>

          {/* Contenteditable Editor */}
          <div
            ref={editorRef}
            className="rte-editor"
            contentEditable
            suppressContentEditableWarning
            onKeyUp={updateActiveFormats}
            onMouseUp={updateActiveFormats}
            onSelect={updateActiveFormats}
          />

          <div className="info-edit-actions">
            <button
              className="btn btn-primary"
              onClick={handleSaveClick}
              disabled={saving}
            >
              {saving ? t('info.saving') : t('buttons.save')}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleCancelClick}
              disabled={saving}
            >
              {t('buttons.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div className="info-content" ref={contentRef}>
          {html
            ? <div dangerouslySetInnerHTML={{ __html: html }} />
            : <p className="info-empty">{t('info.empty')}</p>
          }
        </div>
      )}

      {!isEditing && html && (
        <div className="info-pdf-download">
          <button className="btn btn-pdf" onClick={handleDownloadPdf}>
            {t('info.downloadPdf')}
          </button>
        </div>
      )}
    </div>
  );
}

export default InfoPage;
